import { Data } from "@angular/router";

export enum CheckWinningsRequestStatus {
  Pending = "pending",
  Processing = "processing",
  CompletedWithWinnings = "completed_with_winnings",
  CompletedEmpty = "completed_empty",
  Failed = "failed",
  TimedOut = "timed_out",
}

export const CHECK_WINNINGS_STATUS_FIELD = "request_status";
export const CHECK_WINNINGS_CLIENT_TIMEOUT_MS = 120_000;

const TERMINAL_STATUSES = new Set<string>([
  CheckWinningsRequestStatus.CompletedWithWinnings,
  CheckWinningsRequestStatus.CompletedEmpty,
  CheckWinningsRequestStatus.Failed,
  CheckWinningsRequestStatus.TimedOut,
]);

const PROCESSING_STATUSES = new Set<string>([
  CheckWinningsRequestStatus.Pending,
  CheckWinningsRequestStatus.Processing,
]);

export function isCheckWinningsRequestStatus(value: unknown): value is CheckWinningsRequestStatus {
  return typeof value === "string" && (TERMINAL_STATUSES.has(value) || PROCESSING_STATUSES.has(value));
}

export function isCheckWinningsProcessingStatus(status: CheckWinningsRequestStatus | "unknown"): boolean {
  return status === "unknown" || PROCESSING_STATUSES.has(status);
}

export function isCheckWinningsTerminalStatus(status: CheckWinningsRequestStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

export function isCheckWinningsRetryableStatus(status: CheckWinningsRequestStatus): boolean {
  return status === CheckWinningsRequestStatus.Failed || status === CheckWinningsRequestStatus.TimedOut;
}

function hasWinningsSummaryData(doc: Data): boolean {
  return !!(
    doc?.cash_won_sum ||
    doc?.credit_won_sum ||
    doc?.points_won_sum ||
    doc?.luckydip_won ||
    doc?.gift_won_details ||
    doc?.cash_wom ||
    doc?.credit_won ||
    doc?.points_won
  );
}

function readExplicitStatus(doc: Data | null | undefined): CheckWinningsRequestStatus | null {
  const rawStatus = doc?.[CHECK_WINNINGS_STATUS_FIELD] ?? doc?.check_winnings_status;
  return isCheckWinningsRequestStatus(rawStatus) ? rawStatus : null;
}

/**
 * Resolves the member-facing check-winnings outcome.
 * Never maps slow/missing data to completed_empty — only an explicit backend status may do that.
 */
export function resolveCheckWinningsRequestStatus(
  firestoreDoc: Data | null | undefined,
  apiData: Data | null | undefined
): CheckWinningsRequestStatus | "unknown" {
  const explicitStatus = readExplicitStatus(firestoreDoc);
  if (explicitStatus) {
    return explicitStatus;
  }

  if (apiData?.response?.claim_event_status === 1) {
    return CheckWinningsRequestStatus.CompletedWithWinnings;
  }

  if (firestoreDoc && hasWinningsSummaryData(firestoreDoc)) {
    return CheckWinningsRequestStatus.CompletedWithWinnings;
  }

  return "unknown";
}
